'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { AnimatePresence, motion, useInView } from 'motion/react'

import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { poppins } from '~/font'
import { SignUpModal } from '~/lib/navbar'
import Feature1Image from './images/feature-1.webp'
import Feature2Image from './images/feature-2.webp'
import Feature3Image from './images/feature-3.webp'

const features = [
  {
    title: 'Akses Konten Pembelajaran Berkualitas',
    description: 'Lebih dari 1.000 buku cerita berjenjang, kuis latihan, dan proyek.',
    image: Feature1Image,
  },
  {
    title: 'Pengalaman belajar yang memicu pemikiran',
    description:
      'Aktivitas yang membantu siswa menjelaskan ide dan membentuk argumen dengan bukti, bukan hanya menjawab kumpulan soal-soal dengan cepat.',
    image: Feature2Image,
  },
  {
    title: 'Seru dan berpusat pada anak',
    description:
      'Gamifikasi untuk menjaga motivasi dan pembiasaan belajar mandiri. Aman dan intuitif untuk usia ≤12 tahun.',
    image: Feature3Image,
  },
]

type Feature = (typeof features)[number]

function ScrollTrigger({ index, setActiveIndex }: { index: number; setActiveIndex: (i: number) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { margin: '-40% 0px -40% 0px' })

  useEffect(() => {
    if (isInView) setActiveIndex(index)
  }, [isInView, index, setActiveIndex])

  return (
    <div
      ref={ref}
      aria-hidden
      className='pointer-events-none absolute w-full'
      style={{ top: `${index * 100}vh`, height: '100vh' }}
    />
  )
}

function ProgressBar({ activeIndex }: { activeIndex: number }) {
  return (
    <div className='flex shrink-0 flex-col items-center gap-1.25'>
      {features.map((_, i) => (
        <motion.div
          key={i}
          className='bg-neutral-white w-0.75 rounded-full'
          animate={{
            height: i === activeIndex ? 44 : 18,
            opacity: i === activeIndex ? 1 : i < activeIndex ? 0.45 : 0.2,
          }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      ))}
    </div>
  )
}

function FeatureText({ feature, onOpenSignUp }: { feature: Feature; onOpenSignUp: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={{ duration: 0.32, ease: 'easeOut' }}
      className='flex flex-col gap-4'
    >
      <h3 className={cn(typographyVariants({ variant: 'headline-lg' }), 'text-neutral-white')}>{feature.title}</h3>
      <p className={cn(typographyVariants({ variant: 'body-lg' }), 'text-neutral-white')}>{feature.description}</p>
      <div className='mt-2'>
        <Button variant='secondary' onClick={onOpenSignUp}>
          Mulai Gratis
        </Button>
      </div>
    </motion.div>
  )
}

interface FeaturesProps {
  className?: string
}

export function Features({ className }: FeaturesProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [signUpOpen, setSignUpOpen] = useState(false)
  const handleSetActive = useCallback((i: number) => setActiveIndex(i), [])
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const activeFeature = features[activeIndex] ?? features[0]!

  return (
    <section className={cn('w-full rounded-t-[48px] bg-purple-100', poppins.className, className)}>
      <SignUpModal open={signUpOpen} onOpenChange={setSignUpOpen} />
      {/* ─── DESKTOP: sticky scroll ─── */}
      <div className='relative hidden md:block' style={{ height: `${features.length * 100}vh` }}>
        <div className='sticky top-0 flex h-screen items-center overflow-hidden'>
          <div className='mx-auto flex w-full max-w-7xl items-center px-2'>
            {/* Left: progress bar + animated text */}
            <div className='flex w-1/2 items-center gap-8 pr-12'>
              <ProgressBar activeIndex={activeIndex} />
              <AnimatePresence mode='wait'>
                <FeatureText key={activeIndex} feature={activeFeature} onOpenSignUp={() => setSignUpOpen(true)} />
              </AnimatePresence>
            </div>

            {/* Right: animated image */}
            <div className='flex w-1/2 items-center justify-center'>
              <AnimatePresence mode='wait'>
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, scale: 0.94, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.94, y: -16 }}
                  transition={{ duration: 0.38, ease: 'easeOut' }}
                >
                  <Image
                    src={activeFeature.image}
                    alt={activeFeature.title}
                    draggable={false}
                    className='h-auto w-full object-contain select-none'
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Invisible scroll triggers */}
        {features.map((_, i) => (
          <ScrollTrigger key={i} index={i} setActiveIndex={handleSetActive} />
        ))}
      </div>

      {/* ─── MOBILE: sticky scroll (same mechanism as desktop) ─── */}
      <div className='relative md:hidden' style={{ height: `${features.length * 100}dvh` }}>
        <div className='sticky top-0 flex h-dvh flex-col items-center justify-center gap-6 overflow-hidden py-10'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={`img-${activeIndex}`}
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -20 }}
              transition={{ duration: 0.38, ease: 'easeOut' }}
              className='w-full'
            >
              <Image
                src={activeFeature.image}
                alt={activeFeature.title}
                draggable={false}
                className='h-auto max-h-72 w-full object-contain select-none'
              />
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode='wait'>
            <motion.div
              key={`text-${activeIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}
              className='flex w-full flex-col gap-4 px-6 text-center'
            >
              <h3 className={cn(typographyVariants({ variant: 'headline-lg' }), 'text-neutral-white')}>
                {activeFeature.title}
              </h3>
              <p className={cn(typographyVariants({ variant: 'body-lg' }), 'text-neutral-white')}>
                {activeFeature.description}
              </p>
              <Button className='w-full' variant='secondary' onClick={() => setSignUpOpen(true)}>
                Mulai Gratis
              </Button>
            </motion.div>
          </AnimatePresence>
        </div>

        {features.map((_, i) => (
          <ScrollTrigger key={i} index={i} setActiveIndex={handleSetActive} />
        ))}
      </div>
    </section>
  )
}
